(() => {
  const initFaqs = (root = document) => {
    root.querySelectorAll('[data-vn-faq]').forEach((faq) => {
      if (faq.dataset.vnReady === 'true') return;
      faq.dataset.vnReady = 'true';

      faq.addEventListener('toggle', (event) => {
        const active = event.target;
        if (!(active instanceof HTMLDetailsElement) || !active.open) return;

        faq.querySelectorAll('details[open]').forEach((item) => {
          if (item !== active) item.removeAttribute('open');
        });
      }, true);
    });
  };

  const getJudgeMeReviewTarget = () => document.querySelector(
    '#judgeme_product_reviews, [id*="judgeme_product_reviews"], .jdgm-widget.jdgm-review-widget, .jdgm-rev-widg, [class*="jdgm-rev-widg"]'
  );

  const getProductReviewTarget = () => getJudgeMeReviewTarget()
    || document.querySelector('[data-vn-product-reviews]');

  const syncJudgeMeReviewSummary = () => {
    const target = getJudgeMeReviewTarget();
    if (!target) return;

    const reviewText = target.textContent?.replace(/\s+/g, ' ').trim() || '';
    const ratingMatch = reviewText.match(/\b([0-5](?:[.,][0-9]+)?)\s*(?:out of|\/)\s*5\b/i);
    const countMatch = reviewText.match(/\b([0-9][0-9,]*)\s+reviews?\b/i);
    const rating = ratingMatch?.[1]?.replace(',', '.');
    const count = countMatch?.[1];

    if (!rating && !count) return;
    document.querySelectorAll('[data-vn-review-summary]').forEach((summary) => {
      const ratingNode = summary.querySelector('[data-vn-review-rating]');
      const countNode = summary.querySelector('[data-vn-review-count]');
      if (rating && ratingNode) ratingNode.textContent = rating;
      if (count && countNode) countNode.textContent = `${count} reviews`;
    });
  };

  const initReviewLinks = () => {
    if (document.documentElement.dataset.vnReviewLinksReady === 'true') return;
    document.documentElement.dataset.vnReviewLinksReady = 'true';

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-vn-review-link]');
      if (!link) return;

      const target = getProductReviewTarget();
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }, 450);
    });

    window.setTimeout(syncJudgeMeReviewSummary, 700);
    window.setTimeout(syncJudgeMeReviewSummary, 2200);
  };

  const getMainAddButton = () => document.querySelector('product-info .product-form__submit');

  const getVisiblePrice = () => {
    const bundlePrice = document.querySelector('product-info .qb-tier.active [data-qb-price]')
      || document.querySelector('product-info [data-vn-bundle-option].is-selected [data-vn-bundle-total]');
    if (bundlePrice?.textContent?.trim()) return bundlePrice.textContent.trim();
    const price = document.querySelector('product-info [id^="price-"] .price-item--sale')
      || document.querySelector('product-info [id^="price-"] .price-item--regular');
    return price?.textContent?.trim();
  };

  const formatBundleMoney = (amountInCents, currencyCode) => {
    const currency = currencyCode || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amountInCents / 100);
    } catch {
      return `${(amountInCents / 100).toFixed(2)} ${currency}`;
    }
  };

  const normalizeBundleSectionIds = (sections = []) => sections
    .map((section) => typeof section === 'string' ? section : (section.section || section.id))
    .filter(Boolean)
    .slice(0, 5);

  const BUNDLE_DISCOUNT_CODES = {
    2: 'VERYNOVBUNDLE8',
    3: 'VERYNOVBUNDLE12',
  };

  const updateCartBundleDiscount = async ({ discountCode, sections = [], sectionsUrl = window.location.pathname }) => {
    const sectionIds = normalizeBundleSectionIds(sections);
    const body = {
      discount: discountCode,
      attributes: { _vn_bundle_code: discountCode, _vn_bundle_codes: '' },
    };
    if (sectionIds.length) {
      body.sections = sectionIds;
      body.sections_url = sectionsUrl.startsWith('/') ? sectionsUrl : window.location.pathname;
    }

    const response = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    const cartState = await response.json();
    if (!response.ok || cartState.status || cartState.errors) {
      throw new Error(cartState.description || cartState.message || cartState.errors || 'Bundle discount could not be applied.');
    }
    return cartState;
  };

  const resolveCartBundleDiscountCode = (cartState) => {
    let strongestTier = 0;
    (cartState?.items || []).forEach((item) => {
      const bundleTier = Number.parseInt(item.properties?._vn_bundle_tier || '0', 10);
      const quantity = Number.parseInt(item.quantity || '0', 10);
      if (bundleTier === 3 && quantity >= 3) strongestTier = Math.max(strongestTier, 3);
      if (bundleTier === 2 && quantity >= 2) strongestTier = Math.max(strongestTier, 2);
    });
    return BUNDLE_DISCOUNT_CODES[strongestTier] || '';
  };

  const applyBundleDiscountAfterAdd = async ({ state, bundle, cart, sectionsUrl }) => {
    const selectedOption = bundle?.querySelector('[data-vn-bundle-option].is-selected');
    const discountCode = selectedOption?.dataset.discountCode || '';
    if (!discountCode) return state;

    try {
      const discountedState = await updateCartBundleDiscount({
        discountCode,
        sections: cart?.getSectionsToRender?.() || [],
        sectionsUrl,
      });
      return { ...state, ...discountedState, id: state.id };
    } catch (error) {
      console.error(error);
      return state;
    }
  };

  const reconcileBundleDiscount = async ({ cartState, sections = [], sectionsUrl }) => {
    const desiredCode = resolveCartBundleDiscountCode(cartState);
    const activeCode = cartState?.attributes?._vn_bundle_code || cartState?.attributes?._vn_bundle_codes || '';
    if (desiredCode === activeCode || (!desiredCode && !activeCode)) return cartState;

    try {
      return await updateCartBundleDiscount({ discountCode: desiredCode, sections, sectionsUrl });
    } catch (error) {
      console.error(error);
      return cartState;
    }
  };



  const getBundleQuantityInputs = (bundle) => {
    const formId = bundle.dataset.productFormId;
    const fallback = bundle.querySelector('[data-vn-bundle-fallback-quantity]');
    const escapedFormId = window.CSS?.escape ? window.CSS.escape(formId) : formId.replace(/["\\]/g, '\\$&');
    const externalInputs = [...document.querySelectorAll(`input[name="quantity"][form="${escapedFormId}"]`)]
      .filter((input) => input !== fallback);

    if (externalInputs.length) {
      fallback?.removeAttribute('name');
      if (fallback) fallback.disabled = true;
      return externalInputs;
    }

    if (fallback) {
      fallback.name = 'quantity';
      fallback.disabled = false;
      return [fallback];
    }
    return [];
  };

  const updateBundlePrices = (bundle, basePrice) => {
    if (!Number.isFinite(basePrice) || basePrice <= 0) return;
    bundle.dataset.basePrice = String(basePrice);
    const currency = bundle.dataset.currencyCode;

    bundle.querySelectorAll('[data-vn-bundle-option]').forEach((option) => {
      const quantity = Number.parseInt(option.dataset.quantity, 10) || 1;
      const discount = Number.parseInt(option.dataset.discount, 10) || 0;
      const compareTotal = basePrice * quantity;
      const unitDiscount = Math.floor(basePrice * discount / 100);
      const eachPrice = basePrice - unitDiscount;
      const bundleTotal = eachPrice * quantity;
      const compare = option.querySelector('[data-vn-bundle-compare]');
      const saving = option.querySelector('[data-vn-bundle-saving]');
      const total = option.querySelector('[data-vn-bundle-total]');
      const each = option.querySelector('[data-vn-bundle-each]');

      if (compare) {
        compare.hidden = discount === 0;
        compare.textContent = discount ? formatBundleMoney(compareTotal, currency) : '';
      }
      if (saving) {
        saving.hidden = discount === 0;
        saving.textContent = discount ? `Save ${formatBundleMoney(compareTotal - bundleTotal, currency)}` : '';
      }
      if (total) total.textContent = formatBundleMoney(bundleTotal, currency);
      if (each) each.textContent = `${formatBundleMoney(eachPrice, currency)} each`;
    });
  };

  const updateBundleSubmitLabel = (bundle, quantity) => {
    const submit = document.getElementById(`ProductSubmitButton-${bundle.dataset.sectionId}`);
    const label = submit?.querySelector('span');
    if (!submit || !label || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
    if (submit.dataset.vnSimpleLabel === 'true') {
      label.textContent = window.variantStrings?.addToCart || 'Add to Cart';
      return;
    }
    const bundleUnit = bundle.dataset.bundleKind === 'insole' ? 'Pairs' : 'Pack';
    const selectedTotal = bundle.querySelector('[data-vn-bundle-option].is-selected [data-vn-bundle-total]')?.textContent?.trim();
    const visiblePrice = selectedTotal || getVisiblePrice();
    const defaultLabel = visiblePrice ? `Add to Cart — ${visiblePrice}` : (submit.dataset.vnDefaultLabel || window.variantStrings?.addToCart || 'Add to Cart');
    submit.dataset.vnDefaultLabel = defaultLabel;
    label.textContent = quantity > 1
      ? (bundleUnit === 'Pairs'
        ? `Add ${quantity} Pairs — ${visiblePrice || ''}`.trim()
        : `Add ${quantity}-Pack — ${visiblePrice || ''}`.trim())
      : defaultLabel;
  };

  const syncBundleSelection = (bundle, quantity) => {
    let selectedOption = null;
    bundle.querySelectorAll('[data-vn-bundle-option]').forEach((option) => {
      const isSelected = Number.parseInt(option.dataset.quantity, 10) === quantity;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      if (isSelected) selectedOption = option;
    });
    const tierInput = bundle.querySelector('[data-vn-bundle-tier]');
    if (tierInput) {
      const isBundle = quantity > 1 && selectedOption && !selectedOption.disabled;
      tierInput.disabled = !isBundle;
      tierInput.value = isBundle ? String(quantity) : '';
    }
    updateBundleSubmitLabel(bundle, quantity);
  };

  const syncBundleAvailability = (bundle) => {
    const inputs = getBundleQuantityInputs(bundle);
    const quantityInput = inputs[0];
    const minimum = Number.parseInt(quantityInput?.min || quantityInput?.dataset.min || '1', 10) || 1;
    const maximumValue = quantityInput?.max || quantityInput?.dataset.max;
    const maximum = maximumValue ? Number.parseInt(maximumValue, 10) : null;

    bundle.querySelectorAll('[data-vn-bundle-option]').forEach((option) => {
      const quantity = Number.parseInt(option.dataset.quantity, 10) || 1;
      const unavailable = quantity < minimum || (Number.isFinite(maximum) && quantity > maximum);
      option.disabled = unavailable;
      option.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
    });

    const currentQuantity = Number.parseInt(quantityInput?.value || '1', 10) || 1;
    const currentOption = bundle.querySelector(`[data-vn-bundle-option][data-quantity="${currentQuantity}"]`);
    if (currentOption && !currentOption.disabled) {
      syncBundleSelection(bundle, currentQuantity);
      return;
    }

    bundle.querySelectorAll('[data-vn-bundle-option]').forEach((option) => {
      option.classList.remove('is-selected');
      option.setAttribute('aria-checked', 'false');
    });
    updateBundleSubmitLabel(bundle, currentQuantity);
  };

  const setBundleQuantity = (bundle, quantity) => {
    const inputs = getBundleQuantityInputs(bundle);
    inputs.forEach((input) => {
      input.value = String(quantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    syncBundleSelection(bundle, quantity);
  };

  const initBundles = (root = document) => {
    root.querySelectorAll('[data-vn-bundle]').forEach((bundle) => {
      if (bundle.dataset.vnReady === 'true') return;
      bundle.dataset.vnReady = 'true';

      const inputs = getBundleQuantityInputs(bundle);
      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          const quantity = Number.parseInt(input.value || '1', 10) || 1;
          syncBundleSelection(bundle, quantity);
        });
      });

      bundle.addEventListener('click', (event) => {
        const option = event.target.closest('[data-vn-bundle-option]');
        if (!option || option.disabled) return;
        setBundleQuantity(bundle, Number.parseInt(option.dataset.quantity, 10) || 1);
      });

      updateBundlePrices(bundle, Number.parseInt(bundle.dataset.basePrice, 10));
      syncBundleAvailability(bundle);
    });

    if (document.documentElement.dataset.vnBundleEventsReady === 'true') return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    document.documentElement.dataset.vnBundleEventsReady = 'true';
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = event?.data?.sectionId;
      const variantPrice = Number.parseInt(event?.data?.variant?.price, 10);
      if (!sectionId) return;

      const submit = document.getElementById(`ProductSubmitButton-${sectionId}`);
      if (submit && Number.isFinite(variantPrice)) {
        const formatted = formatBundleMoney(variantPrice, window.Shopify?.currency?.active);
        const label = submit.dataset.vnSimpleLabel === 'true'
          ? (window.variantStrings?.addToCart || 'Add to Cart')
          : `Add to Cart — ${formatted}`;
        submit.dataset.vnDefaultLabel = label;
        if (!submit.disabled && submit.getAttribute('aria-disabled') !== 'true') {
          const labelNode = submit.querySelector('span');
          if (labelNode) labelNode.textContent = label;
        }
      }

      document.querySelectorAll(`[data-vn-bundle][data-section-id="${sectionId}"]`).forEach((bundle) => {
        updateBundlePrices(bundle, variantPrice);
        syncBundleAvailability(bundle);
      });
    });
  };

  const initStickyCart = (root = document) => {
    const bar = root.querySelector('[data-vn-sticky-cart]') || document.querySelector('[data-vn-sticky-cart]');
    if (!bar || bar.dataset.vnReady === 'true') return;
    bar.dataset.vnReady = 'true';

    const stickyButton = bar.querySelector('[data-vn-sticky-button]');
    const stickyPrice = bar.querySelector('[data-vn-sticky-price]');
    const stickyVariant = bar.querySelector('[data-vn-sticky-variant]');
    const productForm = document.querySelector('product-info .product-form');
    if (!stickyButton || !productForm) return;
    let syncFrame = 0;

    const sync = () => {
      const mainButton = getMainAddButton();
      const price = getVisiblePrice();
      if (price && stickyPrice) stickyPrice.textContent = price;
      const selectedValueNodes = document.querySelectorAll('variant-selects [data-selected-value]');
      if (stickyVariant && selectedValueNodes.length) {
        const variantText = [...selectedValueNodes].map((node) => node.textContent.trim()).filter(Boolean).join(' · ');
        if (variantText) stickyVariant.textContent = variantText;
      }
      if (!mainButton) return;
      stickyButton.disabled = mainButton.disabled;
      stickyButton.textContent = mainButton.querySelector('span')?.textContent?.trim() || mainButton.textContent.trim();
    };

    const scheduleSync = () => {
      if (syncFrame) return;
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = 0;
        sync();
      });
    };

    stickyButton.addEventListener('click', () => {
      const mainButton = getMainAddButton();
      if (!mainButton || mainButton.disabled) return;
      mainButton.click();
    });

    const observer = new IntersectionObserver(([entry]) => {
      const shouldShow = !entry.isIntersecting && window.scrollY > entry.boundingClientRect.top;
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }, { threshold: .15 });
    observer.observe(productForm);

    const productInfo = document.querySelector('product-info');
    productInfo?.addEventListener('change', scheduleSync);
    productInfo?.addEventListener('vn:bundle-change', scheduleSync);

    const mainButton = getMainAddButton();
    if (mainButton) {
      new MutationObserver(scheduleSync).observe(mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const priceRoot = productInfo?.querySelector('[id^="price-"]');
    if (priceRoot) {
      new MutationObserver(scheduleSync).observe(priceRoot, { childList: true, subtree: true, characterData: true });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, scheduleSync);
    }
    sync();
  };

  const initCartUpsell = () => {
    if (document.documentElement.dataset.vnCartUpsellReady === 'true') return;
    document.documentElement.dataset.vnCartUpsellReady = 'true';

    const syncUpsellControls = (root = document) => {
      root.querySelectorAll('[data-vn-upsell-slider]').forEach((slider) => {
        const list = slider.querySelector('[data-vn-upsell-list]');
        const previous = slider.querySelector('[data-vn-upsell-prev]');
        const next = slider.querySelector('[data-vn-upsell-next]');
        const position = slider.querySelector('[data-vn-upsell-position]');
        const progress = slider.querySelector('[data-vn-upsell-progress]');
        if (!list || !previous || !next) return;

        const cards = [...list.querySelectorAll('[data-vn-upsell-card]')];
        let updateFrame = 0;

        const update = () => {
          const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
          const scrollProgress = maxScroll ? Math.min(1, Math.max(0, list.scrollLeft / maxScroll)) : 0;
          const scrollable = maxScroll > 4;
          const atStart = list.scrollLeft <= 4;
          const atEnd = list.scrollLeft >= maxScroll - 4;
          const currentIndex = cards.length > 1 ? Math.round(scrollProgress * (cards.length - 1)) : 0;
          slider.classList.toggle('is-scrollable', scrollable);
          slider.classList.toggle('is-at-end', !scrollable || atEnd);
          previous.disabled = !scrollable || atStart;
          next.disabled = !scrollable || atEnd;
          if (position) {
            const current = String(Math.min(currentIndex + 1, Math.max(cards.length, 1))).padStart(2, '0');
            const total = String(Math.max(cards.length, 1)).padStart(2, '0');
            position.textContent = `${current} / ${total}`;
          }
          if (progress) progress.style.transform = `scaleX(${cards.length ? (currentIndex + 1) / cards.length : 1})`;
        };

        const scheduleUpdate = () => {
          if (updateFrame) return;
          updateFrame = window.requestAnimationFrame(() => {
            updateFrame = 0;
            update();
          });
        };

        if (list.dataset.vnSliderReady !== 'true') {
          list.dataset.vnSliderReady = 'true';
          list.addEventListener('scroll', scheduleUpdate, { passive: true });
          if ('ResizeObserver' in window) {
            list.vnUpsellResizeObserver = new ResizeObserver(scheduleUpdate);
            list.vnUpsellResizeObserver.observe(list);
          }
        }

        scheduleUpdate();
      });
    };

    const syncUpsellVariantPrice = (card, option) => {
      if (!card || !option) return;
      const price = Number.parseInt(option.dataset.price || '0', 10);
      const compareAtPrice = Number.parseInt(option.dataset.compareAtPrice || '0', 10);
      const discount = Number.parseInt(card.dataset.vnUpsellDiscount || '0', 10);
      if (!price) return;

      const compare = card.querySelector('[data-vn-upsell-compare]');
      const regular = card.querySelector('[data-vn-upsell-regular]');
      const bundlePrice = card.querySelector('[data-vn-upsell-bundle-price]');
      const currency = card.dataset.currencyCode;

      if (compare) {
        compare.hidden = compareAtPrice <= price;
        compare.textContent = compareAtPrice > price ? formatBundleMoney(compareAtPrice, currency) : '';
      }
      if (regular) regular.textContent = formatBundleMoney(price, currency);
      if (bundlePrice && discount) {
        const unitDiscount = Math.floor(price * discount / 100);
        bundlePrice.textContent = formatBundleMoney(price - unitDiscount, currency);
      }
    };

    const addUpsellVariant = async (card, control, variantId) => {
      if (!card || !control || !variantId || card.dataset.vnAdding === 'true') return;

      const drawer = document.querySelector('cart-drawer');
      const message = card.querySelector('[data-vn-upsell-message]');
      const isSelect = control.matches('[data-vn-upsell-variant]');
      const originalLabel = isSelect ? '' : control.textContent;
      const selectedSize = isSelect ? control.selectedOptions[0]?.textContent.trim() : '';

      card.dataset.vnAdding = 'true';
      card.classList.add('is-adding');
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
      control.closest('[data-vn-upsell-list]')?.classList.remove('is-selecting-size');
      if (!isSelect) control.textContent = 'Adding…';
      if (message) message.textContent = selectedSize ? `Adding ${selectedSize}…` : 'Adding…';
      drawer?.setActiveElement(control);

      try {
        const sections = drawer?.getSectionsToRender().map((section) => section.id) || ['cart-drawer', 'cart-icon-bubble'];
        const shopRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(window.routes?.cart_add_url || `${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this size.');

        if (drawer?.renderContents) {
          drawer.renderContents(data);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        delete card.dataset.vnAdding;
        card.classList.remove('is-adding');
        control.disabled = false;
        control.removeAttribute('aria-busy');
        if (isSelect) {
          control.value = '';
        } else {
          control.textContent = originalLabel;
        }
        if (message) message.textContent = error.message || 'Please try again.';
      }
    };

    document.addEventListener('change', (event) => {
      const select = event.target.closest?.('[data-vn-upsell-variant][data-vn-auto-add]');
      if (!select?.value) return;
      const card = select.closest('[data-vn-upsell-card]');
      syncUpsellVariantPrice(card, select.selectedOptions[0]);
      addUpsellVariant(card, select, select.value);
    });

    document.addEventListener('click', (event) => {
      const sliderButton = event.target.closest('[data-vn-upsell-prev], [data-vn-upsell-next]');
      if (sliderButton) {
        const slider = sliderButton.closest('[data-vn-upsell-slider]');
        const list = slider?.querySelector('[data-vn-upsell-list]');
        if (!list) return;
        const direction = sliderButton.hasAttribute('data-vn-upsell-prev') ? -1 : 1;
        list.scrollBy({ left: direction * Math.max(220, list.clientWidth * .82), behavior: 'smooth' });
        return;
      }

      const button = event.target.closest('[data-vn-upsell-add]');
      if (!button) return;

      const card = button.closest('[data-vn-upsell-card]');
      addUpsellVariant(card, button, button.dataset.variantId);
    });

    document.addEventListener('focusin', (event) => {
      if (!event.target.matches?.('[data-vn-upsell-variant]')) return;
      event.target.closest('[data-vn-upsell-list]')?.classList.add('is-selecting-size');
    });

    document.addEventListener('focusout', (event) => {
      if (!event.target.matches?.('[data-vn-upsell-variant]')) return;
      event.target.closest('[data-vn-upsell-list]')?.classList.remove('is-selecting-size');
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.vnUpsellMutationObserver = new MutationObserver(() => syncUpsellControls(drawer));
      const drawerContent = drawer.querySelector('#CartDrawer');
      if (drawerContent) drawer.vnUpsellMutationObserver.observe(drawerContent, { childList: true });
      syncUpsellControls(drawer);
    }
  };

  const init = (root = document) => {
    document.documentElement.classList.add('vn-ready');
    initFaqs(root);
    initReviewLinks();
    initBundles(root);
    initStickyCart(root);
    // Reference cart owns upsell interactions.
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
